import React, { useRef, useEffect, useState, useCallback } from 'react';

// ─── CSS injected as a style block ───────────────────────────────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500&display=swap');

.tc-root {
  --ink: #0f0f0f;
  --ink2: #3a3a3a;
  --ink3: #7a7a7a;
  --surf: #ffffff;
  --surf2: #f4f4f4;
  --surf3: #ebebeb;
  --accent: #5b4cff;
  --accent-light: #ece9ff;
  --accent-dark: #3d31d4;
  --success: #12a05a;
  --warn: #e55a00;
  --danger: #d42b2b;
  --border: rgba(0,0,0,0.1);
  --border-med: rgba(0,0,0,0.16);
  --radius: 8px;
  --radius-lg: 14px;
  --shadow-sm: 0 1px 3px rgba(0,0,0,.09),0 1px 2px rgba(0,0,0,.07);
  --shadow-md: 0 4px 16px rgba(0,0,0,.10),0 1px 4px rgba(0,0,0,.07);
  --shadow-lg: 0 12px 40px rgba(0,0,0,.13),0 2px 8px rgba(0,0,0,.07);
  --font-display: 'Space Grotesk', system-ui, sans-serif;
  --font-body: 'Inter', system-ui, sans-serif;
  --panel-w: 236px;
  --right-w: 264px;
  --topbar-h: 54px;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.5;
  color: var(--ink);
  background: var(--surf2);
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
}
.tc-root *, .tc-root *::before, .tc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* TOPBAR */
.tc-topbar {
  height: var(--topbar-h);
  background: var(--surf);
  border-bottom: 1px solid var(--border);
  display: flex; align-items: center; padding: 0 18px; gap: 12px;
  box-shadow: var(--shadow-sm); flex-shrink: 0; z-index: 10;
}
.tc-logo {
  font-family: var(--font-display);
  font-size: 20px; font-weight: 800; color: #b07d1a;
  letter-spacing: -0.5px; display: flex; align-items: center; gap: 7px;
  white-space: nowrap;
}
.tc-topbar-center { flex:1; display:flex; align-items:center; justify-content:center; gap:5px; flex-wrap:wrap; }
.tc-tool-btn {
  display:flex; align-items:center; gap:5px;
  padding:5px 11px; border-radius:var(--radius);
  border:1px solid var(--border); background:var(--surf); color:var(--ink2);
  font-family:var(--font-body); font-size:12px; font-weight:500;
  cursor:pointer; transition:all .15s; white-space:nowrap;
}
.tc-tool-btn:hover { background:var(--surf2); border-color:var(--border-med); }
.tc-tool-btn.active { background:var(--accent-light); border-color:var(--accent); color:var(--accent); }
.tc-btn-sm {
  padding:6px 11px; border-radius:var(--radius);
  border:1px solid var(--border); background:var(--surf); color:var(--ink2);
  font-family:var(--font-body); font-size:12px; font-weight:500;
  cursor:pointer; transition:all .15s; white-space:nowrap;
}
.tc-btn-sm:hover { background:var(--surf2); }
.tc-btn-sm.danger { color:var(--danger); border-color:#f5c0c0; }
.tc-btn-sm.danger:hover { background:#fff5f5; }
.tc-divider-v { width:1px; height:22px; background:var(--border); flex-shrink:0; }
.tc-btn-export {
  display:flex; align-items:center; gap:6px;
  padding:7px 16px; border-radius:var(--radius);
  background:var(--accent); color:#fff;
  font-family:var(--font-body); font-size:12px; font-weight:600;
  border:none; cursor:pointer; transition:all .15s;
  box-shadow:0 2px 8px rgba(91,76,255,.3); white-space:nowrap;
}
.tc-btn-export:hover { background:var(--accent-dark); }

/* LAYOUT */
.tc-layout {
  display:grid;
  grid-template-columns: var(--panel-w) 1fr var(--right-w);
  flex:1; overflow:hidden;
}

/* PANELS */
.tc-panel {
  background:var(--surf); border-right:1px solid var(--border);
  display:flex; flex-direction:column; overflow:hidden;
}
.tc-panel-right { border-right:none; border-left:1px solid var(--border); }
.tc-tab-nav { display:flex; border-bottom:1px solid var(--border); padding:0 4px; flex-shrink:0; }
.tc-tab {
  flex:1; padding:10px 5px; text-align:center;
  font-size:11px; font-weight:500; color:var(--ink3);
  cursor:pointer; border-bottom:2px solid transparent; transition:all .15s;
}
.tc-tab:hover { color:var(--ink2); }
.tc-tab.active { color:var(--accent); border-bottom-color:var(--accent); }
.tc-panel-scroll { flex:1; overflow-y:auto; padding:12px; }
.tc-panel-scroll::-webkit-scrollbar { width:4px; }
.tc-panel-scroll::-webkit-scrollbar-thumb { background:var(--surf3); border-radius:2px; }

.tc-section-hd {
  font-size:10px; font-weight:600; text-transform:uppercase;
  letter-spacing:.7px; color:var(--ink3); margin-bottom:7px; margin-top:14px;
}
.tc-section-hd:first-child { margin-top:0; }

/* UPLOAD ZONE */
.tc-upload-zone {
  border:1.5px dashed var(--border-med); border-radius:var(--radius-lg);
  padding:18px 12px; text-align:center; cursor:pointer;
  transition:all .18s; background:var(--surf2); color:var(--ink3);
}
.tc-upload-zone:hover { border-color:var(--accent); background:var(--accent-light); color:var(--accent); }
.tc-upload-zone .icon { font-size:22px; margin-bottom:5px; display:block; }
.tc-upload-zone p { font-size:12px; line-height:1.4; }
.tc-upload-zone small { font-size:10px; color:var(--ink3); display:block; margin-top:3px; }

/* SWATCHES */
.tc-swatch-row { display:flex; flex-wrap:wrap; gap:6px; }
.tc-swatch {
  width:26px; height:26px; border-radius:50%;
  cursor:pointer; border:2px solid transparent; transition:all .15s; position:relative;
}
.tc-swatch.active::after {
  content:''; position:absolute; inset:-3px; border-radius:50%;
  border:2px solid var(--accent);
}
.tc-swatch.light { box-shadow:inset 0 0 0 1px rgba(0,0,0,.15); }

/* SHIRT GRID */
.tc-shirt-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px; margin-top:4px; }
.tc-shirt-thumb {
  border-radius:var(--radius); overflow:hidden;
  border:2px solid var(--border); cursor:pointer; transition:all .15s;
  background:var(--surf2);
}
.tc-shirt-thumb.active { border-color:var(--accent); box-shadow:0 0 0 2px var(--accent-light); }
.tc-shirt-thumb canvas { display:block; width:100%; aspect-ratio:.85; }
.tc-shirt-thumb .lbl { font-size:10px; padding:3px 5px; text-align:center; color:var(--ink3); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* DESIGN GRID */
.tc-design-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
.tc-design-item {
  border-radius:var(--radius); border:1px solid var(--border);
  padding:8px 6px; cursor:pointer; text-align:center;
  background:var(--surf2); transition:all .15s; user-select:none;
}
.tc-design-item:hover { border-color:var(--accent); background:var(--accent-light); transform:translateY(-1px); box-shadow:var(--shadow-sm); }
.tc-design-item canvas { width:100%; aspect-ratio:1; display:block; border-radius:4px; }
.tc-design-item .lbl { font-size:10px; color:var(--ink3); margin-top:4px; }

/* CANVAS AREA */
.tc-canvas-area {
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:20px; background:#e8e8e8;
  background-image:radial-gradient(circle at 1px 1px, rgba(0,0,0,.07) 1px, transparent 0);
  background-size:18px 18px; position:relative; overflow:hidden;
}
.tc-canvas-wrapper { position:relative; }
.tc-main-canvas {
  border-radius:var(--radius-lg); box-shadow:var(--shadow-lg);
  display:block;
  max-width:calc(100vw - var(--panel-w) - var(--right-w) - 80px);
  max-height:calc(100vh - var(--topbar-h) - 40px);
}
.tc-main-canvas.tool-select { cursor:default; }
.tc-main-canvas.tool-text   { cursor:text; }

/* CANVAS HINT */
.tc-canvas-hint {
  position:absolute; bottom:14px; left:50%; transform:translateX(-50%);
  background:rgba(15,15,15,.7); color:#fff; font-size:11px;
  padding:5px 12px; border-radius:20px; white-space:nowrap; pointer-events:none;
  backdrop-filter:blur(4px); opacity:1; transition:opacity .4s;
}
.tc-canvas-hint.hide { opacity:0; }

/* RIGHT PANEL */
.tc-prop-section { padding-bottom:14px; border-bottom:1px solid var(--border); margin-bottom:14px; }
.tc-prop-section:last-child { border-bottom:none; }
.tc-prop-row { display:flex; align-items:center; gap:8px; margin-bottom:7px; }
.tc-prop-label { font-size:11px; color:var(--ink3); min-width:44px; font-weight:500; }
.tc-prop-input {
  flex:1; padding:5px 8px; border-radius:var(--radius);
  border:1px solid var(--border); background:var(--surf);
  color:var(--ink); font-family:var(--font-body); font-size:12px;
  outline:none; transition:border-color .15s;
}
.tc-prop-input:focus { border-color:var(--accent); box-shadow:0 0 0 2px var(--accent-light); }
input[type="range"].tc-prop-input { padding:0; border:none; background:transparent; cursor:pointer; }
input[type="color"].tc-color-pick { width:28px; height:28px; padding:2px; border-radius:6px; border:1px solid var(--border); cursor:pointer; background:transparent; }
.tc-style-btns { display:flex; gap:4px; }
.tc-style-btn {
  width:28px; height:28px; display:flex; align-items:center; justify-content:center;
  border-radius:6px; border:1px solid var(--border); background:var(--surf2);
  cursor:pointer; font-size:13px; font-weight:700; transition:all .12s; color:var(--ink2);
  user-select:none;
}
.tc-style-btn:hover { background:var(--surf3); }
.tc-style-btn.active { background:var(--accent-light); border-color:var(--accent); color:var(--accent); }

/* LAYERS */
.tc-layer-item {
  display:flex; align-items:center; gap:7px;
  padding:6px 8px; border-radius:var(--radius);
  cursor:pointer; border:1px solid transparent; transition:all .12s; font-size:12px;
}
.tc-layer-item:hover { background:var(--surf2); }
.tc-layer-item.active { background:var(--accent-light); border-color:rgba(91,76,255,.2); }
.tc-layer-name { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--ink2); }
.tc-layer-count {
  display:inline-flex; align-items:center; justify-content:center;
  min-width:18px; height:18px; padding:0 4px;
  background:var(--surf2); border-radius:9px; font-size:10px; font-weight:600; color:var(--ink3);
}

/* PERSON PREVIEW */
.tc-person-canvas-wrap { border-radius:var(--radius-lg); overflow:hidden; border:1px solid var(--border); background:var(--surf2); margin-top:8px; }
.tc-person-canvas-wrap canvas { display:block; width:100%; }

/* ADD TEXT BTN */
.tc-btn-add-text {
  width:100%; padding:8px; border-radius:var(--radius);
  background:var(--accent); color:#fff; border:none;
  font-family:var(--font-body); font-size:12px; font-weight:600;
  cursor:pointer; transition:all .15s; margin-top:6px;
  box-shadow:0 2px 8px rgba(91,76,255,.25);
}
.tc-btn-add-text:hover { background:var(--accent-dark); }
.tc-btn-add-text.success { background:var(--success); box-shadow:0 2px 8px rgba(18,160,90,.25); }
.tc-btn-add-text.success:hover { background:#0e8a4e; }

/* SNACKBAR */
.tc-snackbar {
  position:fixed; bottom:22px; left:50%; transform:translateX(-50%) translateY(10px);
  background:var(--ink); color:#fff; padding:8px 18px;
  border-radius:20px; font-size:12px; font-weight:500;
  z-index:9999; opacity:0; transition:all .25s; pointer-events:none;
  box-shadow:var(--shadow-md); white-space:nowrap;
}
.tc-snackbar.show { opacity:1; transform:translateX(-50%) translateY(0); }

/* EMPTY */
.tc-empty { padding:14px 10px; text-align:center; color:var(--ink3); font-size:12px; line-height:1.5; }

/* ZOOM CONTROLS */
.tc-zoom-controls {
  position:absolute; bottom:14px; right:14px;
  display:flex; gap:6px; align-items:center;
}
.tc-zoom-btn {
  width:30px; height:30px; border-radius:50%;
  background:var(--surf); border:1px solid var(--border);
  display:flex; align-items:center; justify-content:center;
  cursor:pointer; font-size:16px; font-weight:600;
  color:var(--ink2); box-shadow:var(--shadow-sm); transition:all .15s;
  user-select:none;
}
.tc-zoom-btn:hover { background:var(--surf2); }
.tc-zoom-label {
  font-size:11px; font-weight:600; color:var(--ink3);
  background:var(--surf); padding:4px 8px; border-radius:12px;
  border:1px solid var(--border); box-shadow:var(--shadow-sm);
}

/* OPACITY SLIDER IN PROPS */
.tc-opacity-row { display:flex; align-items:center; gap:8px; margin-bottom:7px; }

/* RESIZE HANDLE VISUAL - just for UX hint on selected */
.tc-resize-hint { font-size:10px; color:var(--ink3); margin-top:4px; text-align:center; }
`;

// ─── Preset shape drawers ─────────────────────────────────────────────────────
const PRESETS = [
  { name:'Star',     draw:(c,cx,cy,r,col)=>{ c.save();c.fillStyle=col;c.beginPath();for(let i=0;i<5;i++){const a=Math.PI/2+i*4*Math.PI/5,b=a+2*Math.PI/5;i===0?c.moveTo(cx+r*Math.cos(a),cy-r*Math.sin(a)):c.lineTo(cx+r*Math.cos(a),cy-r*Math.sin(a));c.lineTo(cx+r*.42*Math.cos(b),cy-r*.42*Math.sin(b));}c.closePath();c.fill();c.restore();}},
  { name:'Heart',    draw:(c,cx,cy,r,col)=>{ c.save();c.fillStyle=col;c.beginPath();c.moveTo(cx,cy+r*.8);c.bezierCurveTo(cx-r*1.1,cy,cx-r*1.1,cy-r*.9,cx,cy-r*.25);c.bezierCurveTo(cx+r*1.1,cy-r*.9,cx+r*1.1,cy,cx,cy+r*.8);c.fill();c.restore();}},
  { name:'Lightning',draw:(c,cx,cy,r,col)=>{ c.save();c.fillStyle=col;c.beginPath();c.moveTo(cx+r*.1,cy-r);c.lineTo(cx-r*.5,cy);c.lineTo(cx-.05,cy-.05);c.lineTo(cx-r*.25,cy+r);c.lineTo(cx+r*.5,cy-r*.05);c.lineTo(cx+r*.05,cy+r*.05);c.closePath();c.fill();c.restore();}},
  { name:'Crown',    draw:(c,cx,cy,r,col)=>{ c.save();c.fillStyle=col;c.beginPath();c.moveTo(cx-r,cy+r*.4);c.lineTo(cx-r,cy-r*.2);c.lineTo(cx-r*.3,cy+r*.1);c.lineTo(cx,cy-r);c.lineTo(cx+r*.3,cy+r*.1);c.lineTo(cx+r,cy-r*.2);c.lineTo(cx+r,cy+r*.4);c.closePath();c.fill();c.restore();}},
  { name:'Diamond',  draw:(c,cx,cy,r,col)=>{ c.save();c.fillStyle=col;c.beginPath();c.moveTo(cx,cy-r);c.lineTo(cx+r*.65,cy);c.lineTo(cx,cy+r);c.lineTo(cx-r*.65,cy);c.closePath();c.fill();c.restore();}},
  { name:'Circle',   draw:(c,cx,cy,r,col)=>{ c.save();c.strokeStyle=col;c.lineWidth=r*.2;c.beginPath();c.arc(cx,cy,r*.8,0,Math.PI*2);c.stroke();c.restore();}},
  { name:'Arrow',    draw:(c,cx,cy,r,col)=>{ c.save();c.fillStyle=col;c.beginPath();c.moveTo(cx+r,cy);c.lineTo(cx,cy-r*.65);c.lineTo(cx+r*.2,cy-r*.18);c.lineTo(cx-r,cy-r*.18);c.lineTo(cx-r,cy+r*.18);c.lineTo(cx+r*.2,cy+r*.18);c.lineTo(cx,cy+r*.65);c.closePath();c.fill();c.restore();}},
  { name:'Peace',    draw:(c,cx,cy,r,col)=>{ c.save();c.strokeStyle=col;c.lineWidth=r*.17;c.lineCap='round';c.beginPath();c.arc(cx,cy,r*.85,0,Math.PI*2);c.stroke();c.beginPath();c.moveTo(cx,cy-r*.85);c.lineTo(cx,cy+r*.85);c.stroke();c.beginPath();c.moveTo(cx,cy);c.lineTo(cx-r*.73,cy+r*.42);c.stroke();c.beginPath();c.moveTo(cx,cy);c.lineTo(cx+r*.73,cy+r*.42);c.stroke();c.restore();}},
];

const SHIRT_COLORS = [
  {hex:'#ffffff',light:true},{hex:'#f0f0f0',light:true},{hex:'#1a1a1a'},{hex:'#2c3e50'},
  {hex:'#c0392b'},{hex:'#e74c3c'},{hex:'#2980b9'},{hex:'#1abc9c'},
  {hex:'#27ae60'},{hex:'#f39c12'},{hex:'#8e44ad'},{hex:'#e67e22'},
  {hex:'#fd79a8'},{hex:'#fdcb6e'},{hex:'#00b894'},
];

const FONTS = ['Arial','Georgia','Impact','Courier New','Verdana','Times New Roman','Space Grotesk'];
const HANDLE_SIZE = 8;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TShirtDesigner() {
  const canvasRef   = useRef(null);
  const personRef   = useRef(null);
  const nextId      = useRef(1);
  const historyRef  = useRef([]);
  const imgMapRef   = useRef({});   // id -> imgObj (kept outside React state for canvas perf)
  const fnMapRef    = useRef({});   // id -> draw fn

  // canvas drag state (refs to avoid stale closures in events)
  const draggingRef  = useRef(false);
  const dragStartRef = useRef({x:0,y:0});
  const objStartRef  = useRef({x:0,y:0});
  const resizingRef  = useRef(false);
  const resizeHandleRef = useRef('');

  // ── React state ────────────────────────────────────────
  const [objects,    setObjects]    = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [currentTool,setCurrentTool]= useState('select');
  const [shirtImg,   setShirtImg]   = useState(null);   // Image element
  const [shirtColor, setShirtColor] = useState('#cccccc');
  const [personImg,  setPersonImg]  = useState(null);
  const [uploadedShirts, setUploadedShirts] = useState([]);
  const [activeTab,  setActiveTab]  = useState('shirts');
  const [isBold,     setIsBold]     = useState(false);
  const [isItalic,   setIsItalic]   = useState(false);
  const [textAlign,  setTextAlignState] = useState('left');
  const [textInput,  setTextInput]  = useState('');
  const [fontSel,    setFontSel]    = useState('Arial');
  const [fontSize,   setFontSize]   = useState(36);
  const [textColor,  setTextColor]  = useState('#ffffff');
  const [snackMsg,   setSnackMsg]   = useState('');
  const [snackVisible,setSnackVisible] = useState(false);
  const [zoom,       setZoom]       = useState(1);
  const [hintHidden, setHintHidden] = useState(false);
  const [pShirtX,    setPShirtX]    = useState(0);
  const [pShirtY,    setPShirtY]    = useState(0);
  const [pShirtS,    setPShirtS]    = useState(100);
  const [pShirtA,    setPShirtA]    = useState(85);

  // ── Snack helper ───────────────────────────────────────
  const snack = useCallback((msg) => {
    setSnackMsg(msg); setSnackVisible(true);
    setTimeout(() => setSnackVisible(false), 2200);
  }, []);

  // ── Hide hint ──────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setHintHidden(true), 5000);
    return () => clearTimeout(t);
  }, []);

  // ── Canvas rendering ───────────────────────────────────
  const renderCanvas = useCallback((objs, selId, sImg, sColor) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw shirt
    if (sImg) {
      if (sColor !== '#ffffff' && sColor !== '#f0f0f0') {
        ctx.fillStyle = sColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = 'multiply';
        ctx.drawImage(sImg, 0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = 'source-over';
      } else {
        ctx.drawImage(sImg, 0, 0, canvas.width, canvas.height);
      }
    } else {
      drawDefaultShirt(ctx, canvas.width, canvas.height, sColor);
    }

    // Draw objects
    objs.forEach(obj => drawObject(ctx, obj, obj.id === selId));
  }, []);

  function drawDefaultShirt(ctx, w, h, color) {
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.15)'; ctx.shadowBlur = 20;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(w*.22, h*.1);
    ctx.bezierCurveTo(w*.3, h*.03, w*.7, h*.03, w*.78, h*.1);
    ctx.lineTo(w*.97, h*.28); ctx.lineTo(w*.82, h*.33);
    ctx.lineTo(w*.82, h*.9);  ctx.lineTo(w*.18, h*.9);
    ctx.lineTo(w*.18, h*.33); ctx.lineTo(w*.03, h*.28);
    ctx.closePath(); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.lineWidth = 1; ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(w*.22, h*.1);
    ctx.bezierCurveTo(w*.3, h*.03, w*.7, h*.03, w*.78, h*.1);
    ctx.strokeStyle = 'rgba(0,0,0,0.05)'; ctx.lineWidth = 3; ctx.stroke();
    ctx.restore();
  }

  function drawObject(ctx, obj, selected) {
    ctx.save();
    const opacity = obj.opacity !== undefined ? obj.opacity : 1;
    ctx.globalAlpha = opacity;

    if (obj.type === 'text') {
      const style = `${obj.italic?'italic ':''}${obj.bold?'bold ':''}${obj.fontSize||36}px "${obj.font||'Arial'}"`;
      ctx.font = style;
      ctx.fillStyle = obj.color || '#ffffff';
      ctx.textAlign = obj.align || 'left';
      ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 4;
      ctx.fillText(obj.text || '', obj.x, obj.y);
      ctx.shadowBlur = 0;
      if (selected) {
        const m = ctx.measureText(obj.text || '');
        const fw = m.width, fh = obj.fontSize || 36;
        const bx = obj.x - (obj.align==='center'?fw/2 : obj.align==='right'?fw : 0);
        drawSelBox(ctx, bx-4, obj.y-fh-4, fw+8, fh+10);
      }
    } else if (obj.type === 'preset') {
      const fn = fnMapRef.current[obj.id];
      if (fn) fn(ctx, obj.x + obj.w/2, obj.y + obj.h/2, Math.min(obj.w,obj.h)/2, obj.color||'#ffffff');
      if (selected) drawSelBox(ctx, obj.x, obj.y, obj.w, obj.h);
    } else if (obj.type === 'image') {
      const img = imgMapRef.current[obj.id];
      if (img) ctx.drawImage(img, obj.x, obj.y, obj.w, obj.h);
      if (selected) drawSelBox(ctx, obj.x, obj.y, obj.w, obj.h);
    }
    ctx.restore();
  }

  function drawSelBox(ctx, x, y, w, h) {
    ctx.save(); ctx.globalAlpha = 1;
    ctx.strokeStyle = '#5b4cff'; ctx.lineWidth = 1.5;
    ctx.setLineDash([5,3]); ctx.strokeRect(x, y, w, h); ctx.setLineDash([]);
    const pts = [
      [x,y],[x+w/2,y],[x+w,y],[x+w,y+h/2],
      [x+w,y+h],[x+w/2,y+h],[x,y+h],[x,y+h/2]
    ];
    pts.forEach(([px,py]) => {
      ctx.fillStyle='#fff'; ctx.strokeStyle='#5b4cff'; ctx.lineWidth=1.5;
      ctx.fillRect(px-HANDLE_SIZE/2, py-HANDLE_SIZE/2, HANDLE_SIZE, HANDLE_SIZE);
      ctx.strokeRect(px-HANDLE_SIZE/2, py-HANDLE_SIZE/2, HANDLE_SIZE, HANDLE_SIZE);
    });
    ctx.restore();
  }

  // Re-render whenever relevant state changes
  useEffect(() => {
    renderCanvas(objects, selectedId, shirtImg, shirtColor);
  }, [objects, selectedId, shirtImg, shirtColor, renderCanvas]);

  // ── Hit test ───────────────────────────────────────────
  const hitTest = useCallback((p, objs) => {
    const canvas = canvasRef.current; if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    for (let i = objs.length-1; i >= 0; i--) {
      const obj = objs[i];
      if (obj.type === 'text') {
        ctx.font = `${obj.italic?'italic ':''}${obj.bold?'bold ':''}${obj.fontSize||36}px "${obj.font||'Arial'}"`;
        const m = ctx.measureText(obj.text||'');
        const fw = m.width, fh = obj.fontSize||36;
        const bx = obj.x - (obj.align==='center'?fw/2 : obj.align==='right'?fw : 0);
        if (p.x>=bx-4 && p.x<=bx+fw+4 && p.y>=obj.y-fh-4 && p.y<=obj.y+8) return obj;
      } else {
        if (p.x>=obj.x && p.x<=obj.x+obj.w && p.y>=obj.y && p.y<=obj.y+obj.h) return obj;
      }
    }
    return null;
  }, []);

  // hit test resize handles
  const hitResizeHandle = useCallback((p, obj) => {
    if (!obj || obj.type === 'text') return '';
    const { x, y, w, h } = obj;
    const hs = HANDLE_SIZE;
    const handles = {
      'nw':[x,y],'n':[x+w/2,y],'ne':[x+w,y],
      'e':[x+w,y+h/2],'se':[x+w,y+h],'s':[x+w/2,y+h],
      'sw':[x,y+h],'w':[x,y+h/2]
    };
    for (const [handle, [hx,hy]] of Object.entries(handles)) {
      if (Math.abs(p.x-hx) <= hs && Math.abs(p.y-hy) <= hs) return handle;
    }
    return '';
  }, []);

  // ── Canvas pointer events ──────────────────────────────
  const getPos = useCallback((e) => {
    const canvas = canvasRef.current; if (!canvas) return {x:0,y:0};
    const r = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (canvas.width / r.width),
      y: (e.clientY - r.top)  * (canvas.height / r.height)
    };
  }, []);

  const pushHistory = useCallback((objs) => {
    const snapshot = objs.map(o => { const c={...o}; delete c._img; return c; });
    historyRef.current.push(snapshot);
    if (historyRef.current.length > 40) historyRef.current.shift();
  }, []);

  const onMouseDown = useCallback((e) => {
    const p = getPos(e);
    setObjects(prev => {
      if (currentTool === 'text') return prev; // handled by click

      // Check resize handles first on selected object
      if (selectedId) {
        const selObj = prev.find(o => o.id === selectedId);
        if (selObj) {
          const handle = hitResizeHandle(p, selObj);
          if (handle) {
            resizingRef.current = true;
            resizeHandleRef.current = handle;
            dragStartRef.current = p;
            objStartRef.current = { x:selObj.x, y:selObj.y, w:selObj.w, h:selObj.h };
            return prev;
          }
        }
      }

      const obj = hitTest(p, prev);
      if (obj) {
        pushHistory(prev);
        setSelectedId(obj.id);
        draggingRef.current = true;
        dragStartRef.current = p;
        objStartRef.current = { x:obj.x, y:obj.y, w:obj.w||0, h:obj.h||0 };
      } else {
        setSelectedId(null);
      }
      return prev;
    });
  }, [currentTool, selectedId, getPos, hitTest, hitResizeHandle, pushHistory]);

  const onMouseMove = useCallback((e) => {
    if (!draggingRef.current && !resizingRef.current) return;
    const p = getPos(e);
    const dx = p.x - dragStartRef.current.x;
    const dy = p.y - dragStartRef.current.y;

    if (resizingRef.current) {
      const h = resizeHandleRef.current;
      setObjects(prev => prev.map(o => {
        if (o.id !== selectedId) return o;
        let { x, y, w, h: oh } = objStartRef.current;
        if (h.includes('e')) w = Math.max(20, objStartRef.current.w + dx);
        if (h.includes('s')) oh = Math.max(20, objStartRef.current.h + dy);
        if (h.includes('w')) { x = objStartRef.current.x + dx; w = Math.max(20, objStartRef.current.w - dx); }
        if (h.includes('n')) { y = objStartRef.current.y + dy; oh = Math.max(20, objStartRef.current.h - dy); }
        return { ...o, x, y, w, h: oh };
      }));
      return;
    }

    if (draggingRef.current && selectedId) {
      setObjects(prev => prev.map(o => {
        if (o.id !== selectedId) return o;
        return { ...o, x: objStartRef.current.x + dx, y: objStartRef.current.y + dy };
      }));
    }
  }, [selectedId, getPos]);

  const onMouseUp = useCallback(() => {
    draggingRef.current = false;
    resizingRef.current = false;
  }, []);

  const onDblClick = useCallback((e) => {
    const p = getPos(e);
    setObjects(prev => {
      const obj = hitTest(p, prev);
      if (obj && obj.type === 'text') {
        const nt = window.prompt('Edit text:', obj.text);
        if (nt !== null) {
          pushHistory(prev);
          return prev.map(o => o.id === obj.id ? {...o, text:nt} : o);
        }
      }
      return prev;
    });
  }, [getPos, hitTest, pushHistory]);

  const onCanvasClick = useCallback((e) => {
    if (currentTool !== 'text') return;
    const p = getPos(e);
    const txt = textInput.trim();
    if (!txt) { snack('Type text in the right panel first'); return; }
    const newObj = {
      id: nextId.current++, type:'text',
      x:p.x, y:p.y, text:txt,
      font:fontSel, fontSize, color:textColor,
      bold:isBold, italic:isItalic, align:textAlign, opacity:1
    };
    setObjects(prev => { pushHistory(prev); return [...prev, newObj]; });
    snack('Text placed!');
  }, [currentTool, getPos, textInput, fontSel, fontSize, textColor, isBold, isItalic, textAlign, snack, pushHistory]);

  // ── Keyboard shortcuts ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Delete' || e.key === 'Backspace') handleDelete();
      if ((e.ctrlKey||e.metaKey) && e.key === 'z') { e.preventDefault(); handleUndo(); }
      if ((e.ctrlKey||e.metaKey) && e.key === 'd') { e.preventDefault(); handleDuplicate(); }
      if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ── Object actions ─────────────────────────────────────
  const handleDelete = () => {
    setObjects(prev => {
      if (!selectedId) { snack('Select an object first'); return prev; }
      pushHistory(prev);
      setSelectedId(null);
      snack('Deleted');
      return prev.filter(o => o.id !== selectedId);
    });
  };

  const handleDuplicate = () => {
    setObjects(prev => {
      const obj = prev.find(o => o.id === selectedId);
      if (!obj) { snack('Select an object first'); return prev; }
      pushHistory(prev);
      const newId = nextId.current++;
      const copy = { ...obj, id: newId, x: obj.x+20, y: obj.y+20 };
      // copy image ref if any
      if (imgMapRef.current[obj.id]) imgMapRef.current[newId] = imgMapRef.current[obj.id];
      if (fnMapRef.current[obj.id])  fnMapRef.current[newId]  = fnMapRef.current[obj.id];
      setSelectedId(newId);
      snack('Duplicated');
      return [...prev, copy];
    });
  };

  const handleUndo = () => {
    if (!historyRef.current.length) { snack('Nothing to undo'); return; }
    const prev = historyRef.current.pop();
    setObjects(prev);
    setSelectedId(null);
    snack('Undone');
  };

  const handleBringForward = () => {
    setObjects(prev => {
      const i = prev.findIndex(o => o.id === selectedId);
      if (i < 0 || i >= prev.length-1) return prev;
      pushHistory(prev);
      const arr = [...prev];
      [arr[i], arr[i+1]] = [arr[i+1], arr[i]];
      return arr;
    });
  };

  const handleSendBackward = () => {
    setObjects(prev => {
      const i = prev.findIndex(o => o.id === selectedId);
      if (i <= 0) return prev;
      pushHistory(prev);
      const arr = [...prev];
      [arr[i], arr[i-1]] = [arr[i-1], arr[i]];
      return arr;
    });
  };

  const handleClearCanvas = () => {
    setObjects(prev => {
      if (!prev.length) return prev;
      if (!window.confirm('Remove all design elements?')) return prev;
      pushHistory(prev); setSelectedId(null);
      snack('Canvas cleared');
      return [];
    });
  };

  const handleExportPNG = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const savedSel = selectedId;
    // render without selection highlight
    setSelectedId(null);
    requestAnimationFrame(() => {
      renderCanvas(objects, null, shirtImg, shirtColor);
      requestAnimationFrame(() => {
        const link = document.createElement('a');
        link.download = 'threadcraft-design.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
        setSelectedId(savedSel);
        snack('Exported as PNG!');
      });
    });
  };

  // ── Add text ───────────────────────────────────────────
  const handleAddText = () => {
    const txt = textInput.trim();
    if (!txt) { snack('Type something first'); return; }
    const newObj = {
      id: nextId.current++, type:'text',
      x:130, y:280, text:txt,
      font:fontSel, fontSize, color:textColor,
      bold:isBold, italic:isItalic, align:textAlign, opacity:1
    };
    setObjects(prev => { pushHistory(prev); return [...prev, newObj]; });
    snack('Text added to shirt!');
  };

  // ── Shirt upload ───────────────────────────────────────
  const onShirtUpload = (e) => {
    Array.from(e.target.files).forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => {
        const img = new Image();
        img.onload = () => {
          const entry = { img, name: file.name.replace(/\.[^.]+$/,''), id: nextId.current++ };
          setUploadedShirts(prev => {
            const next = [...prev, entry];
            if (!shirtImg) setShirtImg(img);
            return next;
          });
          snack('Shirt added!');
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  // ── Design upload ──────────────────────────────────────
  const onDesignUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const id = nextId.current++;
        imgMapRef.current[id] = img;
        const w = 160, h = 160*(img.height/img.width);
        const newObj = { id, type:'image', x:180, y:200, w, h, name:'Custom', opacity:1 };
        setObjects(prev => { pushHistory(prev); return [...prev, newObj]; });
        snack('Design placed on shirt!');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Add preset ─────────────────────────────────────────
  const addPreset = (p) => {
    const id = nextId.current++;
    fnMapRef.current[id] = p.draw;
    const newObj = { id, type:'preset', fn:null, name:p.name, x:180, y:200, w:120, h:120, color:'#ffffff', opacity:1 };
    setObjects(prev => { pushHistory(prev); return [...prev, newObj]; });
    snack(p.name + ' added!');
  };

  // ── Person upload ──────────────────────────────────────
  const onPersonUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => { setPersonImg(img); snack('Photo loaded!'); };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Person preview rendering ───────────────────────────
  useEffect(() => {
    const c = personRef.current; if (!c || !personImg) return;
    const pw = c.parentElement?.offsetWidth - 2 || 200;
    const scale = pw / personImg.width;
    c.width = pw; c.height = personImg.height * scale;
    const cx = c.getContext('2d');
    cx.drawImage(personImg, 0, 0, c.width, c.height);
    if (shirtImg) {
      const ss = pShirtS / 100, sa = pShirtA / 100;
      const sw = c.width * 0.72 * ss, sh = sw * (shirtImg.height / shirtImg.width);
      const ox = (c.width - sw)/2 + pShirtX;
      const oy = c.height * 0.1 + pShirtY;
      cx.save(); cx.globalAlpha = sa;
      if (shirtColor !== '#ffffff' && shirtColor !== '#f0f0f0') {
        cx.fillStyle = shirtColor; cx.fillRect(ox,oy,sw,sh);
        cx.globalCompositeOperation = 'multiply';
      }
      cx.drawImage(shirtImg, ox, oy, sw, sh);
      cx.restore();
    }
  }, [personImg, shirtImg, shirtColor, pShirtX, pShirtY, pShirtS, pShirtA]);

  const exportPersonPNG = () => {
    const c = personRef.current; if (!c) { snack('Upload a photo first'); return; }
    const link = document.createElement('a');
    link.download = 'tryon-preview.png'; link.href = c.toDataURL('image/png'); link.click();
    snack('Try-on saved!');
  };

  // ── Selected object helpers ────────────────────────────
  const selObj = objects.find(o => o.id === selectedId);

  const updateSelObjProp = (key, val) => {
    setObjects(prev => prev.map(o => o.id === selectedId ? {...o, [key]:val} : o));
  };

  // ── Zoom ───────────────────────────────────────────────
  const zoomIn  = () => setZoom(z => Math.min(2, +(z+0.1).toFixed(1)));
  const zoomOut = () => setZoom(z => Math.max(0.4, +(z-0.1).toFixed(1)));

  // ── Shirt thumbnail canvas helper ──────────────────────
  function ShirtThumb({ entry, active, onClick }) {
    const ref = useRef(null);
    useEffect(() => {
      const c = ref.current; if (!c) return;
      const cx = c.getContext('2d');
      cx.clearRect(0,0,c.width,c.height);
      if (shirtColor !== '#ffffff') {
        cx.fillStyle = shirtColor; cx.fillRect(0,0,c.width,c.height);
        cx.globalCompositeOperation = 'multiply';
      }
      const ratio = Math.min(c.width/entry.img.width, c.height/entry.img.height);
      const w = entry.img.width*ratio, h = entry.img.height*ratio;
      cx.drawImage(entry.img, (c.width-w)/2, (c.height-h)/2, w, h);
      cx.globalCompositeOperation = 'source-over';
    });
    return (
      <div className={`tc-shirt-thumb${active?' active':''}`} onClick={onClick}>
        <canvas ref={ref} width={110} height={128} />
        <div className="lbl">{entry.name.substring(0,14)}</div>
      </div>
    );
  }

  // ── Preset thumbnail canvas helper ─────────────────────
  function PresetThumb({ preset }) {
    const ref = useRef(null);
    useEffect(() => {
      const c = ref.current; if (!c) return;
      const cx = c.getContext('2d');
      cx.fillStyle = '#2c2c2c'; cx.fillRect(0,0,70,70);
      preset.draw(cx, 35, 35, 26, '#5b4cff');
    }, [preset]);
    return (
      <div className="tc-design-item" onClick={() => addPreset(preset)}>
        <canvas ref={ref} width={70} height={70} />
        <div className="lbl">{preset.name}</div>
      </div>
    );
  }

  // ── Selected object props panel ────────────────────────
  function SelObjProps() {
    if (!selObj) return <p className="tc-empty" style={{padding:'6px 0'}}>Click an object on the canvas</p>;

    if (selObj.type === 'text') return (
      <>
        <div className="tc-prop-row">
          <span className="tc-prop-label">Text</span>
          <input className="tc-prop-input" value={selObj.text||''} onChange={e => updateSelObjProp('text', e.target.value)} />
        </div>
        <div className="tc-prop-row">
          <span className="tc-prop-label">Font</span>
          <select className="tc-prop-input" value={selObj.font||'Arial'} onChange={e => updateSelObjProp('font', e.target.value)}>
            {FONTS.map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
        <div className="tc-prop-row">
          <span className="tc-prop-label">Size</span>
          <input type="number" className="tc-prop-input" value={selObj.fontSize||36} min={8} max={200} style={{width:64,flex:'none'}}
            onChange={e => updateSelObjProp('fontSize', +e.target.value)} />
        </div>
        <div className="tc-prop-row">
          <span className="tc-prop-label">Color</span>
          <input type="color" className="tc-color-pick" value={selObj.color||'#ffffff'} onChange={e => updateSelObjProp('color', e.target.value)} />
        </div>
        <div className="tc-prop-row">
          <span className="tc-prop-label">Opacity</span>
          <input type="range" className="tc-prop-input" min={0} max={1} step={0.05} value={selObj.opacity??1} onChange={e => updateSelObjProp('opacity', +e.target.value)} />
          <span style={{fontSize:10,color:'var(--ink3)',minWidth:28}}>{Math.round((selObj.opacity??1)*100)}%</span>
        </div>
      </>
    );

    return (
      <>
        <div className="tc-prop-row">
          <span className="tc-prop-label">X</span>
          <input type="number" className="tc-prop-input" value={Math.round(selObj.x)} onChange={e => updateSelObjProp('x', +e.target.value)} />
          <span className="tc-prop-label" style={{minWidth:'unset'}}>Y</span>
          <input type="number" className="tc-prop-input" value={Math.round(selObj.y)} onChange={e => updateSelObjProp('y', +e.target.value)} />
        </div>
        <div className="tc-prop-row">
          <span className="tc-prop-label">W</span>
          <input type="number" className="tc-prop-input" value={Math.round(selObj.w||100)} min={10} onChange={e => updateSelObjProp('w', +e.target.value)} />
          <span className="tc-prop-label" style={{minWidth:'unset'}}>H</span>
          <input type="number" className="tc-prop-input" value={Math.round(selObj.h||100)} min={10} onChange={e => updateSelObjProp('h', +e.target.value)} />
        </div>
        {selObj.type === 'preset' && (
          <div className="tc-prop-row">
            <span className="tc-prop-label">Color</span>
            <input type="color" className="tc-color-pick" value={selObj.color||'#ffffff'} onChange={e => updateSelObjProp('color', e.target.value)} />
          </div>
        )}
        <div className="tc-prop-row">
          <span className="tc-prop-label">Opacity</span>
          <input type="range" className="tc-prop-input" min={0} max={1} step={0.05} value={selObj.opacity??1} onChange={e => updateSelObjProp('opacity', +e.target.value)} />
          <span style={{fontSize:10,color:'var(--ink3)',minWidth:28}}>{Math.round((selObj.opacity??1)*100)}%</span>
        </div>
        <p className="tc-resize-hint">Drag corner handles on canvas to resize</p>
      </>
    );
  }

  // ── Render ─────────────────────────────────────────────
  return (
    <div className="tc-root">
      <style>{STYLES}</style>

      {/* TOPBAR */}
      <header className="tc-topbar">
        <div className="tc-logo">

        <img id="logo" src="/img/logo.png" className="logo" alt="Gold Vines Logo" />
    
          Gold Vines
        </div>

        <div className="tc-topbar-center">
          <button className={`tc-tool-btn${currentTool==='select'?' active':''}`} onClick={() => setCurrentTool('select')}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M3.5 1L3.5 12.5L6.5 9.5L9.5 15L11 14.2L8 8.5L12.5 8.5L3.5 1Z"/></svg>
            Select
          </button>
          <button className={`tc-tool-btn${currentTool==='text'?' active':''}`} onClick={() => setCurrentTool('text')}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M2 3h12v2H9.5v8h-3V5H2V3z"/></svg>
            Text
          </button>
          <div className="tc-divider-v" />
          <button className="tc-btn-sm" onClick={handleUndo}>↩ Undo</button>
          <button className="tc-btn-sm" onClick={handleDuplicate}>⊕ Dupe</button>
          <button className="tc-btn-sm danger" onClick={handleDelete}>✕ Del</button>
          <div className="tc-divider-v" />
          <button className="tc-btn-sm" onClick={handleBringForward}>↑ Fwd</button>
          <button className="tc-btn-sm" onClick={handleSendBackward}>↓ Back</button>
        </div>

        <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
          <button className="tc-btn-sm" onClick={handleClearCanvas}>Clear</button>
          <button className="tc-btn-export" onClick={handleExportPNG}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M8 10L4 6h3V2h2v4h3L8 10zm-5 3h10v-2H3v2z"/></svg>
            Export PNG
          </button>
        </div>
      </header>

      {/* LAYOUT */}
      <div className="tc-layout">

        {/* ── LEFT PANEL ── */}
        <aside className="tc-panel">
          <nav className="tc-tab-nav">
            {['shirts','designs','person'].map(t => (
              <div key={t} className={`tc-tab${activeTab===t?' active':''}`} onClick={() => setActiveTab(t)}>
                {t==='person'?'Try-On':t.charAt(0).toUpperCase()+t.slice(1)}
              </div>
            ))}
          </nav>

          {/* SHIRTS */}
          {activeTab === 'shirts' && (
            <div className="tc-panel-scroll">
              <div className="tc-section-hd">Upload T-shirt</div>
              <label className="tc-upload-zone">
                <span className="icon">👕</span>
                <p>Click to upload shirt</p>
                <small>PNG with transparent bg works best</small>
                <input type="file" accept="image/*" multiple style={{display:'none'}} onChange={onShirtUpload} />
              </label>

              <div className="tc-section-hd">Shirt Color</div>
              <div className="tc-swatch-row">
                {SHIRT_COLORS.map(c => (
                  <div key={c.hex} className={`tc-swatch${c.light?' light':''}${c.hex===shirtColor?' active':''}`}
                    style={{background:c.hex}} onClick={() => setShirtColor(c.hex)} />
                ))}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:6,marginTop:8}}>
                <span style={{fontSize:11,color:'var(--ink3)'}}>Custom</span>
                <input type="color" className="tc-color-pick" value={shirtColor} onChange={e => setShirtColor(e.target.value)} />
              </div>

              <div className="tc-section-hd">Your Shirts</div>
              <div className="tc-shirt-grid">
                {uploadedShirts.length === 0
                  ? <div className="tc-empty" style={{gridColumn:'1/-1'}}>No shirts uploaded yet</div>
                  : uploadedShirts.map(s => (
                    <ShirtThumb key={s.id} entry={s} active={shirtImg===s.img}
                      onClick={() => { setShirtImg(s.img); }} />
                  ))
                }
              </div>
            </div>
          )}

          {/* DESIGNS */}
          {activeTab === 'designs' && (
            <div className="tc-panel-scroll">
              <div className="tc-section-hd">Upload Your Design</div>
              <label className="tc-upload-zone">
                <span className="icon">🎨</span>
                <p>Upload design / artwork</p>
                <small>PNG · SVG · JPG with transparency</small>
                <input type="file" accept="image/*" style={{display:'none'}} onChange={onDesignUpload} />
              </label>

              <div className="tc-section-hd">Preset Graphics</div>
              <div className="tc-design-grid">
                {PRESETS.map(p => <PresetThumb key={p.name} preset={p} />)}
              </div>
            </div>
          )}

          {/* TRY-ON */}
          {activeTab === 'person' && (
            <div className="tc-panel-scroll">
              <div className="tc-section-hd">Upload Photo</div>
              <label className="tc-upload-zone">
                <span className="icon">🧍</span>
                <p>Upload a person photo</p>
                <small>The shirt will be placed on them</small>
                <input type="file" accept="image/*" style={{display:'none'}} onChange={onPersonUpload} />
              </label>

              {personImg && (
                <>
                  <div className="tc-section-hd">Adjust shirt fit</div>
                  {[
                    {label:'X pos',val:pShirtX,set:setPShirtX,min:-120,max:120},
                    {label:'Y pos',val:pShirtY,set:setPShirtY,min:-150,max:150},
                    {label:'Scale', val:pShirtS,set:setPShirtS,min:30,max:180},
                    {label:'Opacity',val:pShirtA,set:setPShirtA,min:30,max:100},
                  ].map(({label,val,set,min,max}) => (
                    <div className="tc-prop-row" key={label}>
                      <span className="tc-prop-label">{label}</span>
                      <input type="range" className="tc-prop-input" min={min} max={max} value={val} onChange={e => set(+e.target.value)} />
                    </div>
                  ))}
                  <div className="tc-person-canvas-wrap"><canvas ref={personRef} /></div>
                  <button className="tc-btn-add-text success" style={{marginTop:8}} onClick={exportPersonPNG}>
                    Save Try-On Image
                  </button>
                </>
              )}
            </div>
          )}
        </aside>

        {/* ── CANVAS ── */}
        <main className="tc-canvas-area">
          <div className="tc-canvas-wrapper" style={{transform:`scale(${zoom})`,transformOrigin:'center center',transition:'transform .2s'}}>
            <canvas
              ref={canvasRef}
              width={520} height={620}
              className={`tc-main-canvas tool-${currentTool}`}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onDoubleClick={onDblClick}
              onClick={onCanvasClick}
            />
            <div className={`tc-canvas-hint${hintHidden?' hide':''}`}>
              Click to select · Double-click text to edit · Drag to move · Drag handles to resize
            </div>
          </div>

          {/* Zoom controls */}
          <div className="tc-zoom-controls">
            <div className="tc-zoom-btn" onClick={zoomOut}>−</div>
            <span className="tc-zoom-label">{Math.round(zoom*100)}%</span>
            <div className="tc-zoom-btn" onClick={zoomIn}>+</div>
          </div>
        </main>

        {/* ── RIGHT PANEL ── */}
        <aside className="tc-panel tc-panel-right">
          <div style={{padding:'10px 12px 0',borderBottom:'1px solid var(--border)',flexShrink:0}}>
            <div style={{fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'.6px',color:'var(--ink3)',paddingBottom:10}}>
              Properties
            </div>
          </div>
          <div className="tc-panel-scroll">

            {/* ADD TEXT */}
            <div className="tc-prop-section">
              <div className="tc-section-hd" style={{marginTop:0}}>Add text</div>
              <input className="tc-prop-input" placeholder="Type something…" value={textInput}
                onChange={e => setTextInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddText()}
                style={{width:'100%',marginBottom:7}} />
              <div className="tc-prop-row">
                <span className="tc-prop-label">Font</span>
                <select className="tc-prop-input" value={fontSel} onChange={e => setFontSel(e.target.value)}>
                  {FONTS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div className="tc-prop-row">
                <span className="tc-prop-label">Size</span>
                <input type="number" className="tc-prop-input" value={fontSize} min={8} max={200} style={{width:64,flex:'none'}}
                  onChange={e => setFontSize(+e.target.value)} />
                <div className="tc-style-btns" style={{marginLeft:'auto'}}>
                  <div className={`tc-style-btn${isBold?' active':''}`} onClick={() => setIsBold(b => !b)} title="Bold"><b>B</b></div>
                  <div className={`tc-style-btn${isItalic?' active':''}`} onClick={() => setIsItalic(i => !i)} title="Italic"><i>I</i></div>
                </div>
              </div>
              <div className="tc-prop-row">
                <span className="tc-prop-label">Color</span>
                <input type="color" className="tc-color-pick" value={textColor} onChange={e => setTextColor(e.target.value)} />
                <input className="tc-prop-input" value={textColor} style={{width:80,flex:'none'}}
                  onChange={e => /^#[0-9a-fA-F]{6}$/.test(e.target.value) && setTextColor(e.target.value)} />
              </div>
              <div className="tc-prop-row">
                <span className="tc-prop-label">Align</span>
                <div className="tc-style-btns">
                  {[['left','⬅'],['center','☰'],['right','➡']].map(([a,icon]) => (
                    <div key={a} className={`tc-style-btn${textAlign===a?' active':''}`} onClick={() => setTextAlignState(a)}>{icon}</div>
                  ))}
                </div>
              </div>
              <button className="tc-btn-add-text" onClick={handleAddText}>+ Add to shirt</button>
            </div>

            {/* SELECTED OBJECT */}
            <div className="tc-prop-section">
              <div className="tc-section-hd" style={{marginTop:0}}>Selected object</div>
              <SelObjProps />
            </div>

            {/* LAYERS */}
            <div>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
                <span className="tc-section-hd" style={{margin:0}}>Layers</span>
                <span className="tc-layer-count">{objects.length}</span>
              </div>
              {objects.length === 0
                ? <p className="tc-empty">No layers yet</p>
                : [...objects].reverse().map(obj => (
                  <div key={obj.id} className={`tc-layer-item${obj.id===selectedId?' active':''}`}
                    onClick={() => setSelectedId(obj.id)}>
                    <svg style={{width:16,height:16,opacity:.6,flexShrink:0}} viewBox="0 0 16 16" fill="currentColor">
                      {obj.type==='text'
                        ? <path d="M2 3h12v2H9.5v8h-3V5H2V3z"/>
                        : <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                      }
                    </svg>
                    <span className="tc-layer-name">
                      {obj.type==='text' ? (obj.text||'Text').substring(0,20) : (obj.name||obj.type)}
                    </span>
                  </div>
                ))
              }
            </div>
          </div>
        </aside>
      </div>

      {/* SNACKBAR */}
      <div className={`tc-snackbar${snackVisible?' show':''}`}>{snackMsg}</div>
    </div>
  );
}